// De meetwaarden die het projectenoverzicht (ProjectsOverview.jsx) als kolom
// toont en de projectdetailpagina (ProjectDetails.jsx) als kaart. Eén lijst
// voor beide, zodat de twee weergaves niet uit elkaar lopen: een kolom die
// erbij komt verschijnt vanzelf ook op het detailscherm.
//
// De tellingen zelf komen van twee Cloud Functions (getTocOverview en
// getSectorActivityOverview) als een kaart per organisatie; de sleutels
// hieronder verwijzen naar de velden in die kaart.
//
// AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook

/**
 * @typedef {object} ProjectMetric
 * @property {string} key - unieke sleutel binnen de rij of kaart
 * @property {string} label - zichtbaar label, in normale schrijfwijze
 * @property {'count' | 'ratio' | 'context'} type - hoe de waarde uit de tellingen komt
 * @property {string} [countKey] - veld met het aantal (type 'count')
 * @property {string} [lastMsKey] - veld met de laatste registratie in epoch-ms (type 'count')
 * @property {string} [numeratorKey] - veld met de teller (type 'ratio')
 * @property {string} [denominatorKey] - veld met de noemer (type 'ratio')
 * @property {string} [sector] - sector-slug waarvan de contextvragen geteld worden (type 'context')
 */

/**
 * Theory of Change. KOPPELINGEN is een verhouding: hoeveel effecten een
 * compleet pad doelgroep → activiteit → mechanisme → effect hebben.
 * @type {Array<ProjectMetric>}
 */
const TOC_METRICS = [
    { key: 'tocStakeholders', label: 'Doelgroepen', type: 'count', countKey: 'stakeholders' },
    { key: 'tocActivities', label: 'Activiteiten', type: 'count', countKey: 'activities' },
    { key: 'tocMechanisms', label: 'Mechanismen', type: 'count', countKey: 'mechanisms' },
    { key: 'tocEffects', label: 'Effecten', type: 'count', countKey: 'effects' },
    { key: 'tocLinks', label: 'Koppelingen', type: 'ratio', numeratorKey: 'completeEffects', denominatorKey: 'effects' },
]

/**
 * Registraties per sector. Alleen sectoren met eigen registratiecollecties
 * staan hierin — huishoudens zeggen niets over een arbeidsparticipatie-
 * organisatie en andersom.
 * @type {Record<string, Array<ProjectMetric>>}
 */
const SECTOR_METRICS = {
    energiearmoede: [
        { key: 'ctxAnswers', label: 'Contextinfo', type: 'context', sector: 'energiearmoede' },
        { key: 'households', label: 'Huishoudens', type: 'count', countKey: 'households', lastMsKey: 'householdsLastMs' },
        { key: 'materials', label: 'Materialen', type: 'count', countKey: 'materials', lastMsKey: 'materialsLastMs' },
        { key: 'linked', label: 'Gekoppeld', type: 'ratio', numeratorKey: 'householdsWithMaterials', denominatorKey: 'households' },
    ],
    arbeidsparticipatie: [
        { key: 'participants', label: 'Deelnemers', type: 'count', countKey: 'participants', lastMsKey: 'participantsLastMs' },
        { key: 'ladder', label: 'Participatieladder', type: 'count', countKey: 'ladder', lastMsKey: 'ladderLastMs' },
        { key: 'workSkills', label: 'Arbeidsvaardigheden', type: 'count', countKey: 'workSkills', lastMsKey: 'workSkillsLastMs' },
    ],
}

/**
 * De meetwaarden van één sector; een lege lijst voor een sector zonder eigen
 * registraties.
 *
 * @param {string} [sector] - sector-slug
 * @returns {Array<ProjectMetric>}
 */
const sectorMetricsFor = (sector) => SECTOR_METRICS[sector] || []

/**
 * Welke van de gekozen sectoren eigen registraties hebben, in de volgorde
 * waarin ze op de organisatie staan.
 *
 * @param {Array<string>} [selectedSectors] - CompagnyMeta.SelectedSectors
 * @returns {Array<string>}
 */
const sectorsWithRegistrations = (selectedSectors) =>
    (Array.isArray(selectedSectors) ? selectedSectors : [])
        .filter((sector) => Boolean(SECTOR_METRICS[sector]))

export { TOC_METRICS, SECTOR_METRICS, sectorMetricsFor, sectorsWithRegistrations }
export default TOC_METRICS
